<script lang="ts">
  import { onDestroy, onMount } from "svelte";
  import TopMenu from "../../components/TopMenu.svelte";
  import Spinner from "../../components/UI/Spinner.svelte";
  import { dialogTypes, entities, serverlessRoutes } from "../../lib/constants";
  import { getWithJwt, postWithJwt } from "../../lib/requests";
  import { user } from "../../stores/userStore";
  import HabitExpansionPanel from "../../components/Progress/HabitExpansionPanel.svelte";
  import DateIncrementer from "../../components/Progress/DateIncrementer.svelte";
  import localDateKey from "../../lib/localDateKey";
  import Gauge from "../../components/UI/Gauge.svelte";
  import { translate } from "../../lib/translate";
  import { Capacitor } from "@capacitor/core";
  import { currentClient } from "../../stores/currentClient";
  import { alertStore, dialogData, showAlert } from "stores";
  import { isClient } from "../../lib/roles";
  import { ButtonComponent } from "ui";
  import { dragHandleZone, type DndEvent } from "svelte-dnd-action";
  import { animations, getNewPositions, patchWithJwt, errorTracker } from "lib";
  import { flip } from "svelte/animate";

  let habits: Array<any> = [];
  let isLoading = false;
  let selectedDate: Date = new Date();
  let map: object = {};
  let habitData: Array<any> = [];
  const today = new Date();
  let totalSteps = 0;
  let totalCompletedSteps = 0;
  let isCalendarVisible = false;

  $: totalCompletedSteps = calculateCompletedSteps(selectedDate);

  const incrementDate = (n: number) => {
    if (n > 0 && selectedDate >= today) {
      return;
    }
    selectedDate.setDate(selectedDate.getDate() + n);
    selectedDate = selectedDate;
    fetchHabitData();
  };

  const selectDate = (e) => {
    selectedDate = e.detail;
    fetchHabitData();
  };

  const calculateCompletedSteps = (date: Date) => {
    const selectedDateKey = localDateKey(date);
    if (!map[selectedDateKey]) return 0;
    return map[selectedDateKey].reduce(
      (acc, curr) => (acc += curr[Object.keys(curr)[0]].value),
      0
    );
  };

  const fetchData = async () => {
    if (!isLoading && habits.length === 0) {
      const id = $currentClient.id ? $currentClient.id : $user.id;
      isLoading = true;
      const url = `${serverlessRoutes.HABITS}/v2?userId=${id}`;
      // try {
      const response = await getWithJwt(url);
      habits = response.data.items;
      habits.forEach((habit) => (totalSteps += habit.steps));
      fetchHabitData();
      isLoading = false;
      // } catch (err) {}
    }
  };

  const updateDataMap = () => {
    map = {};
    habitData.forEach((item) => {
      item[`habitData`].forEach((dataItem) => {
        const obj = {};
        obj[dataItem[`habitId`]] = {
          value: dataItem.value,
          id: dataItem.id,
        };
        const dateStr = localDateKey(dataItem.createdAt);
        if (!dateStr) return;
        if (map[dateStr]) {
          map[dateStr] = [...map[dateStr], obj];
        } else {
          map[dateStr] = [obj];
        }
      });
    });
    totalCompletedSteps = calculateCompletedSteps(selectedDate);
  };

  const fetchHabitData = async () => {
    const id = $currentClient.id ? $currentClient.id : $user.id;

    const response = await getWithJwt(
      `${serverlessRoutes.HABIT_DATA}?clientId=${id}&createdAt=${localDateKey(selectedDate)}`
    );

    let newData: any[] = [];

    response.data.items.forEach((item: any): void => {
      const { id, value, createdAt, habitId } = item;
      const found = newData.find((el): boolean => el.id === habitId);

      if (found) {
        found.habitData.push({ id, value, createdAt, habitId });
      } else {
        newData.push({
          id: habitId,
          habitData: [{ id, value, createdAt, habitId }],
        });
      }
    });

    habitData = newData;

    updateDataMap();
  };

  const trackNewHabit = async (habit) => {
    const id = isClient($user) ? $user.id : $currentClient.id;
    if (!id) return;
    try {
      const result = await postWithJwt(serverlessRoutes.HABITS, {
        habit,
        clientId: id,
      });
      if (result.error) {
        $showAlert.message = translate("SOMETHING_WENT_WRONG");
        return;
      }
      $showAlert.message = translate("HABIT_SUCCESSFULLY_ADDED");
      habits = [...habits, result.data];
    } catch (err) {}
  };

  const deleteHabit = (event: CustomEvent) => {
    habits = habits.filter((habit) => habit.id !== event.detail.id);
  };

  const updateHabit = (event: CustomEvent) => {
    const updatedHabit = event.detail;
    const index = habits.findIndex((habit) => habit.id === updatedHabit.id);
    habits[index] = { ...habits[index], ...updatedHabit };
    habits = habits;
  };

  const habitsDndConsider = async (event: CustomEvent<DndEvent<any>>): Promise<void> => {
    habits = event.detail.items;
  };

  const habitsDndFinalize = async (event: CustomEvent<DndEvent<any>>): Promise<void> => {
    const partialHabitMap = getNewPositions(event.detail.items);

    if (!partialHabitMap) {
      return;
    }

    const { error, data } = await patchWithJwt(`${serverlessRoutes.HABITS}/v2`, {
      partialHabitMap,
    });

    if (error && !data) {
      alertStore.show(translate("ERROR_CHANGING_ORDER"), "error");
      errorTracker.captureError(error as Error | string, "error", {
        context: "habits",
        action: "reorder_habits"
      });
      return;
    }

    habits = event.detail.items;
  };

  onMount(fetchData);
</script>

{#if !$currentClient.id}
  <TopMenu entity={entities.PROGRESS} />
{/if}
{#if isLoading}
  <Spinner />
{:else}
  <div
    class={`bg-white dark:bg-zinc-800 text-slate-900 dark:text-slate-100 ${!$currentClient.id ? "p-4 " : ""}`}
  >
    <div class="flex-col center-center mt-2 mgb-32">
      <Gauge
        animate={true}
        complete={totalCompletedSteps === 0 ? 1 : (totalCompletedSteps / totalSteps) * 100}
        width={256}
      />
      <div class="flex-col center-center" style="margin-top: -62px;">
        <h2>{translate("COMMITMENT")}</h2>
        <h3 class="text-bold">{totalCompletedSteps}/{totalSteps}</h3>
      </div>
    </div>

    <!-- <div
      class="flex justify-center"
      on:click={() => {
        isCalendarVisible = !isCalendarVisible;
      }}
    > -->
    <!-- <div class="w-[24px]"></div> -->
    <DateIncrementer
      {incrementDate}
      selectedDateDecremented={selectedDate}
      on:select={selectDate}
    />
    <!-- <Svg name="calendar" size={24} on:click={() => { isCalendarVisible = !isCalendarVisible; }}/> -->
    <!-- </div> -->

    <!-- {#if isCalendarVisible}
      <div class="absolute z-50 left-[50%] translate-x-[-50%]">
        <DatePicker
          bind:value={selectedDate}
          max={new Date()}
          on:select={() => (isCalendarVisible = false)}
        />
      </div>
    {/if} -->
    <!-- <DateIncrementer {incrementDate} selectedDateDecremented={selectedDate} /> -->

    <div
      class="flex flex-col gap-4"
      use:dragHandleZone={{
        items: habits,
        flipDurationMs: 0,
        dropTargetStyle: {},
        dropFromOthersDisabled: true,
        type: "habits",
      }}
      on:consider={habitsDndConsider}
      on:finalize={habitsDndFinalize}
    >
      {#each habits as habit (habit.id)}
        <div class="outline-none" animate:flip={animations.flip}>
          <!-- <div>{JSON.stringify(habit, undefined, 2)}</div> -->
          <HabitExpansionPanel
            on:deleteHabit={deleteHabit}
            on:updateHabit={updateHabit}
            steps={habit.steps}
            title={habit.name}
            entityId={habit.id}
            {selectedDate}
            {map}
            {fetchHabitData}
          />
        </div>
      {/each}
    </div>
    <div class="flex flex-row justify-between">
      <div>
        {#if !$currentClient.id}
          <!-- <ButtonComponent on:click={() => ""} isOutline myClasses="mt-4">
            {translate("EXPORT_TO_PDF")}
          </ButtonComponent> -->
        {/if}
      </div>
      <ButtonComponent
        on:click={() => {
          $dialogData.type = dialogTypes.CREATE_EDIT_HABIT;
          $dialogData.data = { executeFunction: trackNewHabit };
        }}
        isOutline
        myClasses="mt-4"
      >
        {translate("TRACK_NEW_HABIT")}
      </ButtonComponent>
    </div>
    {#if Capacitor.isNativePlatform()}
      <div class="mb-12" />
    {/if}
  </div>
{/if}
